/**
 * QUIPWA — WFlashImporter
 *
 * Opens a .wflash file (ZIP), validates manifest + collection.db,
 * creates a deck_instance in _state.db, and inserts initial FSRS states
 * for all cards.
 *
 * Handles:
 *   - Re-import detection by drive_file_id
 *   - SHA-256 media hashing
 *   - Card ID extraction from collection.db
 *
 * Spec sections 2, 6.2 / DSL WFlashImporter
 */

import JSZip from 'jszip'
import { z } from 'zod'
import { v4 as uuidv4 } from 'uuid'
import type { WFlashManifest, Card } from '../../types/domain.ts'
import type { StateDBService } from '../db/state-db-service.ts'
import type { DriveFile } from '../drive/drive-service.ts'
import { getSqlJsSingleton } from '../db/sqljs-loader.ts'
import type { SqlValue } from 'sql.js'

// ─── MANIFEST SCHEMA ─────────────────────────────────────────────────────────

const ManifestSchema = z.object({
  wflash_version: z.literal('1.0'),
  schema_version: z.literal(1),
  deck_id: z.string().uuid(),
  deck_name: z.string().min(1),
  description: z.string().nullable(),
  created_at: z.number().int(),
  updated_at: z.number().int(),
  card_count: z.number().int().min(0),
  note_count: z.number().int().min(0),
  media_count: z.number().int().min(0)
})

// ─── TYPES ───────────────────────────────────────────────────────────────────

export interface ImportResult {
  deck_instance_id: string
  deck_name: string
  card_count: number
  was_reimport: boolean
  errors: string[]
  warnings: string[]
}

export type ReimportAction = 'update' | 'duplicate'

// ─── SHA-256 HELPER ──────────────────────────────────────────────────────────

async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}

// ─── SQLITE LITE READER ───────────────────────────────────────────────────────
// We need to extract card IDs from collection.db without a full SQLite engine.
// Use sql.js for this one-shot read during import.

async function extractCardIds(dbBuffer: ArrayBuffer): Promise<string[]> {
  const SQL = await getSqlJsSingleton()
  const db = new SQL.Database(new Uint8Array(dbBuffer))
  const res = db.exec('SELECT id FROM cards')
  db.close()
  if (res.length === 0 || !res[0]) return []
  return res[0].values.map((row: SqlValue[]) => String(row[0]))
}

async function extractCardRows(dbBuffer: ArrayBuffer): Promise<Card[]> {
  const SQL = await getSqlJsSingleton()
  const db = new SQL.Database(new Uint8Array(dbBuffer))
  const res = db.exec(`SELECT id, note_id, deck_id, template_ord, card_type, created_at FROM cards`)
  db.close()
  if (res.length === 0 || !res[0]) return []
  return res[0].values.map((row: SqlValue[]) => ({
    id: String(row[0]),
    note_id: String(row[1]),
    deck_id: String(row[2]),
    template_ord: Number(row[3]) as 0 | 1,
    card_type: Number(row[4]) as 0 | 1 | 2 | 3,
    created_at: Number(row[5])
  }))
}

// ─── IMPORTER ────────────────────────────────────────────────────────────────

export class WFlashImporter {
  private readonly stateDB: StateDBService
  constructor(stateDB: StateDBService) { this.stateDB = stateDB }

  /**
   * Import a .wflash file from a File/Blob object.
   *
   * @param file              The .wflash file to import
   * @param driveFile         Drive metadata (id, path) — null if local-only import
   * @param onReimport        Called when an existing deck with same drive_file_id found.
   *                          Must return 'update' or 'duplicate'.
   */
  async importFromFile(
    file: File | Blob,
    driveFile: DriveFile | null,
    onReimport?: (deckName: string) => Promise<ReimportAction>
  ): Promise<ImportResult> {
    const errors: string[] = []
    const warnings: string[] = []

    // ── 1. Load ZIP ────────────────────────────────────────────────────────
    let zip: JSZip
    try {
      zip = await JSZip.loadAsync(file)
    } catch {
      return {
        deck_instance_id: '', deck_name: '', card_count: 0,
        was_reimport: false, errors: ['Archivo .wflash inválido: no es un ZIP válido'], warnings
      }
    }

    // ── 2. Parse manifest ─────────────────────────────────────────────────
    const manifestFile = zip.file('manifest.json')
    if (!manifestFile) {
      return {
        deck_instance_id: '', deck_name: '', card_count: 0,
        was_reimport: false, errors: ['manifest.json no encontrado en el .wflash'], warnings
      }
    }

    let manifest: WFlashManifest
    try {
      const raw = await manifestFile.async('string')
      const parsed = ManifestSchema.safeParse(JSON.parse(raw))
      if (!parsed.success) {
        return {
          deck_instance_id: '', deck_name: '', card_count: 0,
          was_reimport: false,
          errors: [`manifest.json inválido: ${parsed.error.issues.map((e: { message: string }) => e.message).join('; ')}`],
          warnings
        }
      }
      manifest = parsed.data as WFlashManifest
    } catch {
      return {
        deck_instance_id: '', deck_name: '', card_count: 0,
        was_reimport: false, errors: ['manifest.json no es JSON válido'], warnings
      }
    }

    // ── 3. Extract collection.db ──────────────────────────────────────────
    const collectionFile = zip.file('collection.db')
    if (!collectionFile) {
      return {
        deck_instance_id: '', deck_name: '', card_count: 0,
        was_reimport: false, errors: ['collection.db no encontrado en el .wflash'], warnings
      }
    }

    const dbBuffer = await collectionFile.async('arraybuffer')

    // ── 4. Extract card IDs ───────────────────────────────────────────────
    let cardIds: string[]
    try {
      cardIds = await extractCardIds(dbBuffer)
    } catch (err) {
      warnings.push(`No se pudieron extraer cards: ${String(err)}`)
      cardIds = []
    }

    if (cardIds.length === 0) {
      warnings.push('El deck no tiene cards. Se creará vacío.')
    }

    // ── 5. Check for reimport ─────────────────────────────────────────────
    let was_reimport = false
    let deck_instance_id = uuidv4()

    if (driveFile) {
      const existing = await this.stateDB.getDeckInstanceByFileId(driveFile.id)
      if (existing) {
        was_reimport = true
        const action = onReimport
          ? await onReimport(existing.deck_name)
          : 'update'

        if (action === 'update') {
          // Reconcile: keep existing deck_instance_id, update cards
          deck_instance_id = existing.id
          await this.stateDB.reconcileCards(existing.id, cardIds)
          await this.stateDB.updateDeckModifiedAt(
            existing.id,
            Math.floor(new Date(driveFile.modifiedTime).getTime() / 1000)
          )
          await this.stateDB.updateCardCount(existing.id, cardIds.length)

          return {
            deck_instance_id: existing.id,
            deck_name: existing.deck_name,
            card_count: cardIds.length,
            was_reimport: true,
            errors,
            warnings
          }
        }
        // action === 'duplicate': fall through to create new instance
      }
    }

    // ── 6. Validate media hashes ──────────────────────────────────────────
    const mediaFolder = zip.folder('media')
    if (mediaFolder) {
      const mediaFiles = Object.keys(zip.files).filter(p => p.startsWith('media/'))
      for (const path of mediaFiles.slice(0, 50)) { // Limit validation to first 50
        const mediaFile = zip.file(path)
        if (!mediaFile) continue
        const buf = await mediaFile.async('arraybuffer')
        const computed = await sha256Hex(buf)
        const filename = path.replace('media/', '')
        if (computed !== filename) {
          warnings.push(`Media hash mismatch: ${filename} (computed: ${computed.slice(0, 8)}...)`)
        }
      }
    }

    // ── 7. Create deck_instance in _state.db ─────────────────────────────
    const nowTs = Math.floor(Date.now() / 1000)
    const inst = await this.stateDB.insertDeckInstance({
      deck_id: manifest.deck_id,
      deck_name: manifest.deck_name,
      wflash_drive_path: driveFile ? driveFile.name : `${manifest.deck_name}.wflash`,
      drive_file_id: driveFile ? driveFile.id : '',
      drive_modified_at: driveFile
        ? Math.floor(new Date(driveFile.modifiedTime).getTime() / 1000)
        : nowTs,
      imported_at: nowTs,
      card_count: cardIds.length
    })
    deck_instance_id = inst.id

    // ── 8. Insert initial FSRS states for all cards ───────────────────────
    await this.stateDB.insertInitialReviewStates(cardIds, deck_instance_id, nowTs)

    // ── 9. Initialize deck_stats row ──────────────────────────────────────
    await this.stateDB.recomputeDeckStats(deck_instance_id)

    return {
      deck_instance_id,
      deck_name: manifest.deck_name,
      card_count: cardIds.length,
      was_reimport,
      errors,
      warnings
    }
  }

  /**
   * Import from an ArrayBuffer (e.g. downloaded from Drive).
   */
  async importFromArrayBuffer(
    buffer: ArrayBuffer,
    driveFile: DriveFile | null,
    onReimport?: (deckName: string) => Promise<ReimportAction>
  ): Promise<ImportResult> {
    const blob = new Blob([buffer], { type: 'application/octet-stream' })
    return this.importFromFile(blob, driveFile, onReimport)
  }
}

// ─── PREVIEW (before confirming import) ──────────────────────────────────────

export interface WFlashPreview {
  manifest: WFlashManifest
  card_count: number
  media_count: number
  sample_cards: Array<{ front: string; back: string }>
  is_valid: boolean
  errors: string[]
}

/**
 * Read a .wflash file and return a preview without committing to _state.db.
 * Used by ImportModal before the user confirms.
 */
export async function previewWFlash(file: File | Blob): Promise<WFlashPreview> {
  const errors: string[] = []

  let zip: JSZip
  try {
    zip = await JSZip.loadAsync(file)
  } catch {
    return {
      manifest: {} as WFlashManifest, card_count: 0, media_count: 0,
      sample_cards: [], is_valid: false, errors: ['Archivo no válido']
    }
  }

  const manifestFile = zip.file('manifest.json')
  if (!manifestFile) {
    return {
      manifest: {} as WFlashManifest, card_count: 0, media_count: 0,
      sample_cards: [], is_valid: false, errors: ['manifest.json no encontrado']
    }
  }

  let manifest: WFlashManifest
  try {
    const raw = await manifestFile.async('string')
    const parsed = ManifestSchema.safeParse(JSON.parse(raw))
    if (!parsed.success) {
      errors.push(...parsed.error.issues.map((e: { message: string }) => e.message))
      return {
        manifest: {} as WFlashManifest, card_count: 0, media_count: 0,
        sample_cards: [], is_valid: false, errors
      }
    }
    manifest = parsed.data as WFlashManifest
  } catch {
    return {
      manifest: {} as WFlashManifest, card_count: 0, media_count: 0,
      sample_cards: [], is_valid: false, errors: ['manifest.json inválido']
    }
  }

  const collectionFile = zip.file('collection.db')
  let sample_cards: Array<{ front: string; back: string }> = []
  let card_count = 0

  if (collectionFile) {
    try {
      const dbBuffer = await collectionFile.async('arraybuffer')
      const cards = await extractCardRows(dbBuffer)
      card_count = cards.length

      // Get sample card texts
      const SQL = await getSqlJsSingleton()
      const db = new SQL.Database(new Uint8Array(dbBuffer))
      const res = db.exec(`
        SELECT nf0.value AS front, nf1.value AS back
        FROM cards c
        JOIN note_fields nf0 ON nf0.note_id = c.note_id AND nf0.field_ord = 0
        JOIN note_fields nf1 ON nf1.note_id = c.note_id AND nf1.field_ord = 1
        LIMIT 3
      `)
      db.close()

      if (res.length > 0 && res[0]) {
        sample_cards = res[0].values.map((row: SqlValue[]) => ({
          front: String(row[0]),
          back: String(row[1])
        }))
      }
    } catch {
      errors.push('No se pudo leer collection.db')
    }
  }

  const media_count = Object.keys(zip.files)
    .filter(p => p.startsWith('media/') && !p.endsWith('/')).length

  return {
    manifest,
    card_count,
    media_count,
    sample_cards,
    is_valid: errors.length === 0,
    errors
  }
}
